import React, { useState, useEffect } from 'react';
import { ChatMessage } from '../../types/chat';
import { useChat } from '../../contexts/ChatContext';
import { PlayIcon, StopIcon, ThumbUpIcon, ThumbDownIcon } from './icons/ChatIcons';
import MarkdownRenderer from './MarkdownRenderer';
import ActionCard from './ActionCard';
import VoiceNotePlayer from './VoiceNotePlayer';
import { formatVoiceNoteDuration } from '../../helpers/voiceNoteHelper';

interface MessageBubbleProps {
  message: ChatMessage;
  autoSpeakEnabled?: boolean;
  autoSpeakActivatedAt?: number | null;
  isLastMessage?: boolean;
  onFeedback?: (feedbackThumb: boolean, feedbackText: string | null) => void;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  autoSpeakEnabled = false,
  autoSpeakActivatedAt = null,
  isLastMessage = false,
  onFeedback,
}) => {
  const isUser = message.role === 'user';
  const isSystem = message.role === 'system';
  const isAgent = message.role === 'agent';
  const { speakingMessageId, speakMessage, cancelSpeech, discardVoiceNote } = useChat();
  // Nota de voz grabada sin conexión que todavía no tiene texto (KAN-225).
  const pendingVoiceNote = isUser && message.voiceNote && !message.content ? message.voiceNote : null;

  const [isSpeakingThis, setIsSpeakingThis] = useState(false);
  const [spokenTimestamp, setSpokenTimestamp] = useState<number | null>(null);
  const [showCommentField, setShowCommentField] = useState(false);
  const [commentText, setCommentText] = useState('');

  // Sincronizar estado local con el estado global
  useEffect(() => {
    setIsSpeakingThis(message.id === speakingMessageId);
  }, [speakingMessageId, message.id]);

  // Reproducir automáticamente SOLO si:
  // - El modo habla está activado
  // - El mensaje es del asistente (no agent: los de soporte no se auto-hablan)
  // - El mensaje tiene contenido
  // - El mensaje NO está en streaming
  // - El mensaje NO se ha reproducido antes (spokenTimestamp es null)
  // - El mensaje ES EL ÚLTIMO mensaje del asistente (evita reproducciones simultáneas)
  // - El mensaje fue creado DESPUÉS de activar el modo auto-speak (autoSpeakActivatedAt)
  useEffect(() => {
    if (
      !isUser &&
      !isSystem &&
      !isAgent &&
      autoSpeakEnabled &&
      message.content &&
      !message.isStreaming &&
      spokenTimestamp === null &&
      isLastMessage &&
      autoSpeakActivatedAt
    ) {
      const messageTime = new Date(message.timestamp).getTime();
      if (messageTime > autoSpeakActivatedAt) {
        // Reproducir usando la función centralizada (garantiza que solo uno hable)
        speakMessage(message.id, message.content);
        setSpokenTimestamp(messageTime);
      }
    }
  }, [message, message.id, message.content, message.isStreaming, autoSpeakEnabled, autoSpeakActivatedAt, isLastMessage, isUser, isSystem, isAgent, spokenTimestamp, speakMessage]);

  const handleSpeakToggle = () => {
    if (isSpeakingThis) {
      // Si está hablando este mensaje, cancelarlo
      cancelSpeech();
    } else {
      // Hablar este mensaje
      const messageTime = new Date(message.timestamp).getTime();
      setSpokenTimestamp(messageTime);
      speakMessage(message.id, message.content);
    }
  };

  const handleThumbUp = () => {
    setShowCommentField(false);
    onFeedback?.(true, null);
  };

  const handleThumbDown = () => {
    setCommentText(message.feedbackText ?? '');
    setShowCommentField(true);
  };

  const handleCancelComment = () => {
    setShowCommentField(false);
  };

  const handleSubmitComment = () => {
    onFeedback?.(false, commentText.trim() || null);
    setShowCommentField(false);
  };

  if (isSystem) {
    return (
      <div className="flex justify-center my-2">
        <div className="bg-paper-2 text-sage text-xs px-3 py-1 rounded">
          {message.content}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4 animate-fade-in`}
    >
      <div
        className={`${isUser ? 'ml-[10px]' : 'mr-[10px]'} w-[95%] rounded-lg px-2 py-2 pb-2 ${
          isUser
            ? 'bg-forest text-paper rounded-br-sm'
            : isAgent
              ? 'bg-paper border border-hairline border-l-2 border-l-sage text-ink rounded-bl-sm shadow-card'
              : 'bg-paper border border-hairline text-ink rounded-bl-sm shadow-card'
        } ${isUser && message.status === 'pendiente' ? 'opacity-60' : ''}`}
      >
        {/* Badge "Soporte" para mensajes del team (rol agent) */}
        {isAgent && (
          <div className="flex items-center gap-1 mb-1">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-sage bg-sage/10 px-1.5 py-0.5 rounded">
              Soporte
            </span>
          </div>
        )}

        {/* Message content */}
        <div className="text-[16px] break-words leading-relaxed prose max-w-none mb-2">
          {isUser && pendingVoiceNote ? (
            <div className="flex items-center gap-2">
              <span aria-hidden>🎤</span>
              <div className="flex-1">
                <div className="font-medium">Nota de voz · {formatVoiceNoteDuration(pendingVoiceNote.durationMs)}</div>
                <div className="text-xs text-paper/80">
                  {pendingVoiceNote.transcribing
                    ? 'Transcribiendo…'
                    : message.status === 'error'
                      ? 'No se pudo entender la nota de voz. Reintentá o descartala.'
                      : 'Se enviará al volver la señal'}
                </div>
              </div>
              <VoiceNotePlayer messageId={message.id} />
              {!pendingVoiceNote.transcribing && (
                <button
                  type="button"
                  onClick={() => { void discardVoiceNote(message.id); }}
                  className="px-2 py-0.5 rounded text-lg leading-none text-paper/80 hover:text-paper hover:bg-paper/10 transition-colors"
                  aria-label="Descartar nota de voz"
                  title="Descartar nota de voz"
                >
                  ×
                </button>
              )}
            </div>
          ) : isUser ? (
            <div className="whitespace-pre-wrap">
              {message.voiceNote && (
                <span className="mr-1 text-xs" title="Dictado como nota de voz" aria-label="Dictado como nota de voz">🎤</span>
              )}
              {message.content}
              {message.voiceNote && (
                <span className="ml-2 inline-flex align-middle">
                  <VoiceNotePlayer messageId={message.id} />
                </span>
              )}
            </div>
          ) : (
            <MarkdownRenderer content={message.content} />
          )}
        </div>

        {/* Chat actions (ver/confirmar) — side-channel from the backend `done` event */}
        {!isUser && message.actions && message.actions.length > 0 && (
          <ActionCard actions={message.actions} />
        )}

        {/* Streaming indicator */}
        {message.isStreaming && (
          <div className="flex items-center space-x-1 mb-1">
            <div className="w-1.5 h-1.5 bg-current rounded-pixel animate-pulse"></div>
            <div className="w-1.5 h-1.5 bg-current rounded-pixel animate-pulse delay-75"></div>
            <div className="w-1.5 h-1.5 bg-current rounded-pixel animate-pulse delay-150"></div>
          </div>
        )}

        {/* Footer con timestamp y controles de audio */}
        <div className="flex items-center justify-between mt-1">
          {/* Estado de envío (cola offline, KAN-218): pendiente / error */}
          {isUser && message.status === 'pendiente' && (
            <span className="flex items-center gap-1 text-paper/70 text-xs" title="Se enviará al reconectar">
              🕐 Pendiente
            </span>
          )}
          {isUser && message.status === 'error' && (
            <span className="flex items-center gap-1 text-xs font-semibold text-red-200" title="No se envió — tocá Reintentar">
              ⚠️ No enviado
            </span>
          )}

          {/* Timestamp */}
          <div className={`text-xs ${isUser ? 'text-paper/70' : 'text-sage'}`}>
            {new Date(message.timestamp).toLocaleTimeString('es-ES', {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </div>

          {/* Audio controls y feedback - Solo para mensajes del asistente */}
          {!isUser && message.content && (
            <div className="flex items-center gap-1.5">
              {isSpeakingThis ? (
                <button
                  onClick={handleSpeakToggle}
                  className="p-1 bg-danger text-paper rounded-md hover:bg-danger/90 transition-colors"
                  title="Detener"
                >
                  <StopIcon className="h-3 w-3" />
                </button>
              ) : (
                <button
                  onClick={handleSpeakToggle}
                  className="p-1 bg-forest text-paper rounded-md hover:bg-forest-mid transition-colors"
                  title="Leer en voz alta"
                >
                  <PlayIcon className="h-3 w-3" />
                </button>
              )}
              {!message.isStreaming && onFeedback && (
                <>
                  <button
                    onClick={handleThumbUp}
                    className={`p-1 rounded-md transition-colors ${
                      message.feedbackThumb === true
                        ? 'bg-success text-paper'
                        : 'bg-paper-2 text-sage hover:bg-success/10 hover:text-success'
                    }`}
                    title="Útil"
                  >
                    <ThumbUpIcon className="h-3 w-3" />
                  </button>
                  <button
                    onClick={handleThumbDown}
                    className={`p-1 rounded-md transition-colors ${
                      message.feedbackThumb === false
                        ? 'bg-danger text-paper'
                        : 'bg-paper-2 text-sage hover:bg-danger/10 hover:text-danger'
                    }`}
                    title="No útil"
                  >
                    <ThumbDownIcon className="h-3 w-3" />
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Campo de comentario para feedback negativo */}
        {showCommentField && !isUser && (
          <div className="mt-2 flex flex-col gap-1.5">
            <textarea
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              rows={2}
              placeholder="Contanos qué se puede mejorar..."
              className="w-full text-sm px-2 py-1.5 border border-hairline rounded-lg focus:outline-none focus:ring-1 focus:ring-forest/30 focus:border-forest resize-none text-ink bg-paper placeholder:text-sage/70"
              autoFocus
            />
            <div className="flex justify-end gap-1.5">
              <button
                onClick={handleCancelComment}
                className="px-2 py-1 text-xs text-ink bg-paper-2 rounded-lg hover:bg-hairline/60 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSubmitComment}
                className="px-2 py-1 text-xs text-paper bg-forest rounded-lg hover:bg-forest-mid transition-colors"
              >
                Enviar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MessageBubble;
